import React, { useState, useEffect } from 'react';
import {
  Table,
  Card,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  message,
  Popconfirm,
  Statistic,
  Row,
  Col
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  EyeOutlined
} from '@ant-design/icons';
import api from '../../utils/api';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { TextArea } = Input;
const { Option } = Select;

const SmsRecordList = () => {
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });
  const [filters, setFilters] = useState({});
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [auditModalVisible, setAuditModalVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [currentRecord, setCurrentRecord] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [form] = Form.useForm();
  const [searchForm] = Form.useForm();

  // 加载数据
  const loadData = async (params = {}) => {
    setLoading(true);
    try {
      const response = await api.get('/admin/sms-records', {
        params: {
          page: pagination.current,
          limit: pagination.pageSize,
          ...filters,
          ...params
        }
      });

      if (response.code === 200) {
        setDataSource(response.data.list);
        setPagination({
          ...pagination,
          total: response.data.pagination.total,
          current: response.data.pagination.page
        });
      }
    } catch (error) {
      message.error('加载数据失败');
    } finally {
      setLoading(false);
    }
  };

  // 加载统计数据
  const loadStatistics = async () => {
    try {
      const response = await api.get('/admin/sms-records/statistics', {
        params: filters
      });

      if (response.code === 200) {
        setStatistics(response.data);
      }
    } catch (error) {
      console.error('加载统计数据失败', error);
    }
  };

  useEffect(() => {
    loadData();
    loadStatistics();
  }, []);

  // 搜索
  const handleSearch = (values) => {
    const newFilters = {
      ...values,
      startDate: values.dateRange?.[0]?.format('YYYY-MM-DD'),
      endDate: values.dateRange?.[1]?.format('YYYY-MM-DD')
    };
    delete newFilters.dateRange;

    setFilters(newFilters);
    setPagination({ ...pagination, current: 1 });
    loadData({ ...newFilters, page: 1 });
    loadStatistics();
  };

  // 重置搜索
  const handleReset = () => {
    searchForm.resetFields();
    setFilters({});
    setPagination({ ...pagination, current: 1 });
    loadData({ page: 1 });
    loadStatistics();
  };

  // 查看详情
  const handleViewDetail = (record) => {
    setCurrentRecord(record);
    setDetailModalVisible(true);
  };

  // 审核
  const handleAudit = (record) => {
    setCurrentRecord(record);
    form.setFieldsValue({
      auditStatus: 'approved',
      auditRemark: ''
    });
    setAuditModalVisible(true);
  };

  // 提交审核
  const handleAuditSubmit = async () => {
    try {
      const values = await form.validateFields();
      const response = await api.put(
        `/admin/sms-records/${currentRecord.id}/audit`,
        values
      );

      if (response.code === 200) {
        message.success('审核成功');
        setAuditModalVisible(false);
        loadData();
        loadStatistics();
      }
    } catch (error) {
      message.error('审核失败');
    }
  };

  // 批量审核
  const handleBatchAudit = async (auditStatus) => {
    if (selectedRowKeys.length === 0) {
      message.warning('请选择要审核的记录');
      return;
    }

    Modal.confirm({
      title: `确认批量${auditStatus === 'approved' ? '通过' : '拒绝'}？`,
      content: `将对选中的 ${selectedRowKeys.length} 条记录进行审核`,
      onOk: async () => {
        try {
          const response = await api.put('/admin/sms-records/batch/audit', {
            ids: selectedRowKeys,
            auditStatus,
            auditRemark: `批量${auditStatus === 'approved' ? '通过' : '拒绝'}`
          });

          if (response.code === 200) {
            message.success(response.message);
            setSelectedRowKeys([]);
            loadData();
            loadStatistics();
          }
        } catch (error) {
          message.error('批量审核失败');
        }
      }
    });
  };

  // 删除
  const handleDelete = async (id) => {
    try {
      const response = await api.delete(`/admin/sms-records/${id}`);
      if (response.code === 200) {
        message.success('删除成功');
        loadData();
        loadStatistics();
      }
    } catch (error) {
      message.error('删除失败');
    }
  };

  // 批量删除
  const handleBatchDelete = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请选择要删除的记录');
      return;
    }

    Modal.confirm({
      title: '确认删除？',
      content: `将删除选中的 ${selectedRowKeys.length} 条记录，此操作不可恢复`,
      okType: 'danger',
      onOk: async () => {
        try {
          const response = await api.delete('/admin/sms-records/batch/delete', {
            data: { ids: selectedRowKeys }
          });

          if (response.code === 200) {
            message.success(response.message);
            setSelectedRowKeys([]);
            loadData();
            loadStatistics();
          }
        } catch (error) {
          message.error('批量删除失败');
        }
      }
    });
  };

  // 表格列定义
  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 80
    },
    {
      title: '设备ID',
      dataIndex: 'device_id',
      width: 120,
      ellipsis: true
    },
    {
      title: '交易代码',
      dataIndex: 'transaction_code',
      width: 130,
      ellipsis: true,
      render: (code) => code || '-'
    },
    {
      title: '发送方',
      dataIndex: 'sender',
      width: 120,
      ellipsis: true,
      render: (sender) => sender || '-'
    },
    {
      title: '手机号',
      dataIndex: 'account',
      width: 120,
      ellipsis: true,
      render: (account) => account || '-'
    },
    {
      title: '金额',
      dataIndex: 'amount',
      width: 100,
      render: (amount) => amount ? `Ksh ${amount.toLocaleString()}` : '-'
    },
    {
      title: '余额',
      dataIndex: 'balance',
      width: 100,
      render: (balance) => balance ? `Ksh ${balance.toLocaleString()}` : '-'
    },
    {
      title: '交易时间',
      dataIndex: 'datetime',
      width: 150,
      render: (datetime) => datetime || '-'
    },
    {
      title: '审核状态',
      dataIndex: 'audit_status',
      width: 100,
      render: (status) => {
        const statusMap = {
          pending: { color: 'orange', text: '待审核' },
          approved: { color: 'green', text: '已通过' },
          rejected: { color: 'red', text: '已拒绝' }
        };
        const config = statusMap[status] || { color: 'default', text: status };
        return <Tag color={config.color}>{config.text}</Tag>;
      }
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      width: 160,
      render: (time) => dayjs(time).format('YYYY-MM-DD HH:mm:ss')
    },
    {
      title: '操作',
      key: 'action',
      width: 180,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetail(record)}
          >
            详情
          </Button>
          {record.audit_status === 'pending' && (
            <Button
              type="link"
              size="small"
              onClick={() => handleAudit(record)}
            >
              审核
            </Button>
          )}
          <Popconfirm
            title="确认删除？"
            onConfirm={() => handleDelete(record.id)}
          >
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  // 行选择配置
  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys)
  };

  return (
    <div style={{ padding: 24 }}>
      {/* 统计卡片 */}
      {statistics && (
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Card>
              <Statistic
                title="总记录数"
                value={statistics.totalCount}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          {statistics.byStatus.map((stat) => (
            <Col span={6} key={stat.status}>
              <Card>
                <Statistic
                  title={stat.statusName}
                  value={stat.count}
                  valueStyle={{
                    color:
                      stat.status === 'approved'
                        ? '#52c41a'
                        : stat.status === 'rejected'
                        ? '#f5222d'
                        : '#faad14'
                  }}
                />
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* 搜索表单 */}
      <Card style={{ marginBottom: 16 }}>
        <Form form={searchForm} layout="inline" onFinish={handleSearch}>
          <Form.Item name="deviceId" label="设备ID">
            <Input placeholder="请输入设备ID" style={{ width: 200 }} />
          </Form.Item>
          <Form.Item name="account" label="账号">
            <Input placeholder="请输入账号" style={{ width: 200 }} />
          </Form.Item>
          <Form.Item name="auditStatus" label="审核状态">
            <Select placeholder="请选择" style={{ width: 120 }} allowClear>
              <Option value="pending">待审核</Option>
              <Option value="approved">已通过</Option>
              <Option value="rejected">已拒绝</Option>
            </Select>
          </Form.Item>
          <Form.Item name="dateRange" label="日期范围">
            <RangePicker />
          </Form.Item>
          <Form.Item name="keyword" label="关键词">
            <Input placeholder="搜索内容或账号" style={{ width: 200 }} />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
                搜索
              </Button>
              <Button onClick={handleReset} icon={<ReloadOutlined />}>
                重置
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      {/* 操作按钮 */}
      <Card>
        <Space style={{ marginBottom: 16 }}>
          <Button
            type="primary"
            icon={<CheckCircleOutlined />}
            onClick={() => handleBatchAudit('approved')}
            disabled={selectedRowKeys.length === 0}
          >
            批量通过
          </Button>
          <Button
            danger
            icon={<CloseCircleOutlined />}
            onClick={() => handleBatchAudit('rejected')}
            disabled={selectedRowKeys.length === 0}
          >
            批量拒绝
          </Button>
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={handleBatchDelete}
            disabled={selectedRowKeys.length === 0}
          >
            批量删除
          </Button>
          <Button icon={<ReloadOutlined />} onClick={() => loadData()}>
            刷新
          </Button>
        </Space>

        {/* 表格 */}
        <Table
          rowKey="id"
          columns={columns}
          dataSource={dataSource}
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条记录`
          }}
          rowSelection={rowSelection}
          onChange={(newPagination) => {
            setPagination(newPagination);
            loadData({ page: newPagination.current, limit: newPagination.pageSize });
          }}
          scroll={{ x: 1800 }}
        />
      </Card>

      {/* 审核弹窗 */}
      <Modal
        title="审核短信记录"
        open={auditModalVisible}
        onOk={handleAuditSubmit}
        onCancel={() => setAuditModalVisible(false)}
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="auditStatus"
            label="审核结果"
            rules={[{ required: true, message: '请选择审核结果' }]}
          >
            <Select>
              <Option value="approved">通过</Option>
              <Option value="rejected">拒绝</Option>
            </Select>
          </Form.Item>
          <Form.Item name="auditRemark" label="审核备注">
            <TextArea rows={4} placeholder="请输入审核备注" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 详情弹窗 */}
      <Modal
        title="短信记录详情"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            关闭
          </Button>
        ]}
        width={700}
      >
        {currentRecord && (
          <div>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <p><strong>ID：</strong>{currentRecord.id}</p>
              </Col>
              <Col span={12}>
                <p><strong>设备ID：</strong>{currentRecord.device_id}</p>
              </Col>
              <Col span={12}>
                <p><strong>交易代码：</strong>{currentRecord.transaction_code || '-'}</p>
              </Col>
              <Col span={12}>
                <p><strong>发送方：</strong>{currentRecord.sender || '-'}</p>
              </Col>
              <Col span={12}>
                <p><strong>手机号：</strong>{currentRecord.account || '-'}</p>
              </Col>
              <Col span={12}>
                <p><strong>金额：</strong>{currentRecord.amount ? `Ksh ${currentRecord.amount.toLocaleString()}` : '-'}</p>
              </Col>
              <Col span={12}>
                <p><strong>余额：</strong>{currentRecord.balance ? `Ksh ${currentRecord.balance.toLocaleString()}` : '-'}</p>
              </Col>
              <Col span={12}>
                <p><strong>交易时间：</strong>{currentRecord.datetime || '-'}</p>
              </Col>
              <Col span={24}>
                <p><strong>短信内容：</strong></p>
                <div style={{ padding: 12, background: '#f5f5f5', borderRadius: 4, marginBottom: 12, whiteSpace: 'pre-wrap' }}>
                  {currentRecord.content}
                </div>
              </Col>
              <Col span={24}>
                <p>
                  <strong>审核状态：</strong>
                  <Tag
                    color={
                      currentRecord.audit_status === 'approved'
                        ? 'green'
                        : currentRecord.audit_status === 'rejected'
                        ? 'red'
                        : 'orange'
                    }
                    style={{ marginLeft: 8 }}
                  >
                    {currentRecord.audit_status === 'approved'
                      ? '已通过'
                      : currentRecord.audit_status === 'rejected'
                      ? '已拒绝'
                      : '待审核'}
                  </Tag>
                </p>
              </Col>
              {currentRecord.audit_remark && (
                <Col span={24}>
                  <p><strong>审核备注：</strong></p>
                  <div style={{ padding: 12, background: '#f5f5f5', borderRadius: 4, marginBottom: 12 }}>
                    {currentRecord.audit_remark}
                  </div>
                </Col>
              )}
              <Col span={12}>
                <p><strong>创建时间：</strong>{dayjs(currentRecord.created_at).format('YYYY-MM-DD HH:mm:ss')}</p>
              </Col>
              <Col span={12}>
                <p><strong>更新时间：</strong>{dayjs(currentRecord.updated_at).format('YYYY-MM-DD HH:mm:ss')}</p>
              </Col>
            </Row>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default SmsRecordList;
